export type AudioEffectType =
  | 'echo'
  | 'reverb'
  | 'distortion'
  | 'telephone'
  | 'tremolo'
  | 'deepVoice'
  | 'highPitchVoice'
  | 'robot'
  | 'monster'
  | 'walkieTalkie'
  | 'none';

export class AudioEffect {
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;
  private mainGainNode: GainNode | null = null;
  private audioElement: HTMLAudioElement | null = null;

  // Effect-specific nodes
  private delayNode: DelayNode | null = null;
  private feedbackGainNode: GainNode | null = null;
  private convolverNode: ConvolverNode | null = null;
  private distortionNode: WaveShaperNode | null = null;
  private filterNode: BiquadFilterNode | null = null;
  private tremoloGainNode: GainNode | null = null;
  private lfo: OscillatorNode | null = null;
  private voiceChangeFilterNode: BiquadFilterNode | null = null;

  private activeEffect: AudioEffectType = 'none';

  constructor() {}

  initialize(audioElement: HTMLAudioElement): void {
    if (this.audioElement === audioElement) {
      return; // Already initialized for this element
    }

    this.cleanup(); // Clean up any previous instance

    this.audioElement = audioElement;
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    this.sourceNode = this.audioContext.createMediaElementSource(this.audioElement);
    this.mainGainNode = this.audioContext.createGain();
    this.mainGainNode.connect(this.audioContext.destination);

    // By default, apply no effect, which just connects the dry signal
    this.applyEffect('none');
  }

  applyEffect(effectType: AudioEffectType): void {
    if (!this.audioContext || !this.sourceNode || !this.mainGainNode || !this.audioElement) {
      console.error('AudioEffect not initialized.');
      return;
    }

    // Reset playbackRate to default to avoid carrying over pitch/speed changes.
    this.audioElement.playbackRate = 1.0;

    // Disconnect all existing connections from the source to start fresh
    this.sourceNode.disconnect();
    this._disconnectEffectNodes();
    this.activeEffect = effectType;

    // Always connect the dry signal path
    this.sourceNode.connect(this.mainGainNode);

    // Connect the wet signal path based on the selected effect
    switch (effectType) {
      case 'echo':
        this.delayNode = this.audioContext.createDelay();
        this.feedbackGainNode = this.audioContext.createGain();

        this.delayNode.delayTime.value = 0.4;
        this.feedbackGainNode.gain.value = 0.4;

        this.sourceNode.connect(this.delayNode);
        this.delayNode.connect(this.feedbackGainNode);
        this.feedbackGainNode.connect(this.delayNode); // Feedback loop
        this.delayNode.connect(this.mainGainNode);
        break;

      case 'reverb':
        this.convolverNode = this.audioContext.createConvolver();
        this.convolverNode.buffer = this._createReverbImpulseResponse();

        if (this.convolverNode.buffer) {
          this.sourceNode.connect(this.convolverNode);
          this.convolverNode.connect(this.mainGainNode);
        }
        break;

      // Use for "monster" or "robot" voices.
      case 'distortion':
        this.distortionNode = this.audioContext.createWaveShaper();
        this.distortionNode.curve = this._createDistortionCurve(400);
        this.distortionNode.oversample = '4x';

        this.sourceNode.connect(this.distortionNode);
        this.distortionNode.connect(this.mainGainNode);
        break;

      // Use for voices over a "telephone" or "radio".
      case 'telephone':
        this.filterNode = this.audioContext.createBiquadFilter();
        this.filterNode.type = 'lowpass';
        this.filterNode.frequency.setValueAtTime(2000, this.audioContext.currentTime);

        this.sourceNode.connect(this.filterNode);
        this.filterNode.connect(this.mainGainNode);
        break;

      // Use for "ghostly" or "wavering" voices.
      case 'tremolo':
        this.tremoloGainNode = this.audioContext.createGain();
        this.lfo = this.audioContext.createOscillator();

        this.lfo.type = 'sine';
        this.lfo.frequency.value = 5; // Speed of the effect

        this.lfo.connect(this.tremoloGainNode.gain);
        this.lfo.start();

        this.sourceNode.connect(this.tremoloGainNode);
        this.tremoloGainNode.connect(this.mainGainNode);
        break;

      // Use for a deep, booming voice.
      case 'deepVoice':
        if (!this.audioContext || !this.sourceNode || !this.mainGainNode || !this.audioElement) break;

        // Lower the pitch (and speed)
        this.audioElement.playbackRate = 0.75;

        // Use a lowshelf filter to boost bass frequencies
        this.voiceChangeFilterNode = this.audioContext.createBiquadFilter();
        this.voiceChangeFilterNode.type = 'lowshelf';
        this.voiceChangeFilterNode.frequency.setValueAtTime(300, this.audioContext.currentTime); // Boost frequencies below 300Hz
        this.voiceChangeFilterNode.gain.setValueAtTime(6, this.audioContext.currentTime); // Boost by 6 dB

        this.sourceNode.connect(this.voiceChangeFilterNode);
        this.voiceChangeFilterNode.connect(this.mainGainNode);
        break;

      // Use for a high-pitched, "chipmunk" style voice.
      case 'highPitchVoice':
        if (!this.audioContext || !this.sourceNode || !this.mainGainNode || !this.audioElement) break;

        // Raise the pitch (and speed)
        this.audioElement.playbackRate = 1.6;

        // Use a highshelf filter to boost treble frequencies
        this.voiceChangeFilterNode = this.audioContext.createBiquadFilter();
        this.voiceChangeFilterNode.type = 'highshelf';
        this.voiceChangeFilterNode.frequency.setValueAtTime(1000, this.audioContext.currentTime); // Boost frequencies above 1000Hz
        this.voiceChangeFilterNode.gain.setValueAtTime(6, this.audioContext.currentTime); // Boost by 6 dB

        this.sourceNode.connect(this.voiceChangeFilterNode);
        this.voiceChangeFilterNode.connect(this.mainGainNode);
        break;

      // Use for a robotic, stuttering voice.
      case 'robot':
        if (!this.audioContext || !this.sourceNode || !this.mainGainNode || !this.audioElement) break;

        this.audioElement.playbackRate = 0.9;

        this.tremoloGainNode = this.audioContext.createGain();
        this.lfo = this.audioContext.createOscillator();

        this.lfo.type = 'square'; // Sharp, abrupt volume changes
        this.lfo.frequency.value = 20; // Fast stutter

        this.lfo.connect(this.tremoloGainNode.gain);
        this.lfo.start();

        this.sourceNode.connect(this.tremoloGainNode);
        this.tremoloGainNode.connect(this.mainGainNode);
        break;

      // Use for a deep, distorted monster voice.
      case 'monster':
        if (!this.audioContext || !this.sourceNode || !this.mainGainNode || !this.audioElement) break;

        this.audioElement.playbackRate = 0.6; // Lower pitch significantly

        this.distortionNode = this.audioContext.createWaveShaper();
        this.distortionNode.curve = this._createDistortionCurve(800); // Heavy distortion
        this.distortionNode.oversample = '4x';

        this.sourceNode.connect(this.distortionNode);
        this.distortionNode.connect(this.mainGainNode);
        break;

      // Use for a tinny, mid-range walkie-talkie sound.
      case 'walkieTalkie':
        if (!this.audioContext || !this.sourceNode || !this.mainGainNode) break;

        // Filter to a narrow mid-range band
        this.filterNode = this.audioContext.createBiquadFilter();
        this.filterNode.type = 'bandpass';
        this.filterNode.frequency.setValueAtTime(1500, this.audioContext.currentTime);
        this.filterNode.Q.setValueAtTime(4, this.audioContext.currentTime); // Narrow band

        // Add a little crackle
        this.distortionNode = this.audioContext.createWaveShaper();
        this.distortionNode.curve = this._createDistortionCurve(50); // Light distortion
        this.distortionNode.oversample = '2x';

        this.sourceNode.connect(this.filterNode);
        this.filterNode.connect(this.distortionNode);
        this.distortionNode.connect(this.mainGainNode);
        break;

      case 'none':
      default:
        // Only the dry path is connected, no extra nodes needed.
        break;
    }
  }

  /** Disconnects and nullifies only the effect-specific nodes. */
  private _disconnectEffectNodes(): void {
    if (this.delayNode) {
      this.delayNode.disconnect();
      this.delayNode = null;
    }
    if (this.feedbackGainNode) {
      this.feedbackGainNode.disconnect();
      this.feedbackGainNode = null;
    }
    if (this.convolverNode) {
      this.convolverNode.disconnect();
      this.convolverNode = null;
    }
    if (this.distortionNode) {
      this.distortionNode.disconnect();
      this.distortionNode = null;
    }
    if (this.filterNode) {
      this.filterNode.disconnect();
      this.filterNode = null;
    }
    if (this.tremoloGainNode) {
      this.tremoloGainNode.disconnect();
      this.tremoloGainNode = null;
    }
    if (this.lfo) {
      this.lfo.stop();
      this.lfo.disconnect();
      this.lfo = null;
    }
    if (this.voiceChangeFilterNode) {
      this.voiceChangeFilterNode.disconnect();
      this.voiceChangeFilterNode = null;
    }
  }

  private _createReverbImpulseResponse(): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const duration = 2;
    const decay = 5;
    const numChannels = 2;
    const frameCount = sampleRate * duration;

    const buffer = this.audioContext.createBuffer(numChannels, frameCount, sampleRate);

    for (let channel = 0; channel < numChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < frameCount; i++) {
        const progress = i / frameCount;
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - progress, decay);
      }
    }
    return buffer;
  }

  private _createDistortionCurve(amount: number): Float32Array {
    const k = typeof amount === 'number' ? amount : 50;
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    let i = 0;
    let x;
    for (; i < n_samples; ++i) {
      x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }

  async play(): Promise<void> {
    if (!this.audioElement || !this.audioContext) return;

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    try {
      await this.audioElement.play();
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  }

  pause(): void {
    if (!this.audioElement) return;
    this.audioElement.pause();
  }

  cleanup(): void {
    if (this.audioContext) {
      this.sourceNode?.disconnect();
      this.mainGainNode?.disconnect();
      this._disconnectEffectNodes();

      if (this.audioContext.state !== 'closed') {
        this.audioContext.close();
      }
    }

    // Reset properties
    this.audioContext = null;
    this.sourceNode = null;
    this.delayNode = null;
    this.feedbackGainNode = null;
    this.convolverNode = null;
    this.distortionNode = null;
    this.filterNode = null;
    this.tremoloGainNode = null;
    this.lfo = null;
    this.voiceChangeFilterNode = null;
    this.mainGainNode = null;
    this.audioElement = null;
    this.activeEffect = 'none';
  }
}
